import React from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { kgToLb } from "@/lib/units";
import ExerciseIcon from "@/components/ExerciseIcon";

const fmtKg = (kg, isImperial) => {
  if (kg == null) return "";
  const num = Number(kg);
  if (!Number.isFinite(num)) return "";
  if (isImperial) return `${(Math.round(kgToLb(num) * 2) / 2).toFixed(1)} lbs`;
  return `${num.toFixed(1)} kg`;
};

/**
 * Workout history list — read-only display of the user's last N sessions.
 * Renders an Exercise-DB thumbnail (or fallback icon) next to every logged
 * exercise plus a "Cues" deep-link to /exercises?id=<id> when matched.
 */
export default function WorkoutHistory({ history, exerciseFor, isImperial = false }) {
  return (
    <div className="obs-card">
      <div className="overline mb-2">History</div>
      <h3 className="font-heading text-3xl uppercase mb-4">Sessions</h3>
      {history.length === 0 ? (
        <p className="text-zinc-500 text-sm">No workouts yet. Log your first session above.</p>
      ) : (
        <div className="space-y-3">
          {history.slice(0, 10).map((w) => (
            <div key={w.id} className="border border-obsidian-600 rounded-sm p-4 bg-obsidian-900/40">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading text-xl uppercase">{w.title}</div>
                <div className="text-xs text-zinc-500">
                  {new Date(w.created_at).toLocaleString()} · {w.duration_min || "—"} min
                </div>
              </div>
              <div className="space-y-2">
                {w.exercises.map((ex, exIdx) => {
                  const dbEx = exerciseFor(ex.name);
                  const topSet = ex.sets.reduce(
                    (a, b) => (b.weight_kg > (a?.weight_kg ?? -1) ? b : a),
                    null
                  );
                  return (
                    <div
                      key={exIdx}
                      data-testid={`history-exercise-${w.id}-${exIdx}`}
                      className="flex items-center gap-3 py-1"
                    >
                      <ExerciseIcon
                        exercise={dbEx || { name: ex.name, equipment: "", muscles: [] }}
                        size="sm"
                        data-testid={`history-thumb-${w.id}-${exIdx}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-200 truncate">{ex.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          {ex.sets.length} set{ex.sets.length === 1 ? "" : "s"}
                          {topSet ? ` · top ${fmtKg(topSet.weight_kg, isImperial)} × ${topSet.reps}` : ""}
                        </div>
                      </div>
                      {dbEx && (
                        <Link
                          to={`/exercises?id=${dbEx.id}`}
                          data-testid={`history-opendb-${w.id}-${exIdx}`}
                          title="Open in Exercise DB"
                          className="text-[10px] uppercase tracking-widest text-blaze hover:text-white inline-flex items-center gap-1 shrink-0 border border-obsidian-600 hover:border-blaze rounded-sm px-2 py-1 transition-colors"
                        >
                          <BookOpen className="h-3 w-3" /> Cues
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest border-t border-obsidian-600 pt-2">
                Volume: {isImperial
                  ? `${(Math.round(kgToLb(Number(w.total_volume_kg || 0))) ).toLocaleString()} lbs`
                  : `${Number(w.total_volume_kg || 0).toFixed(0)} kg`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
